import { MemberDTO } from '../../services/apiClient';
import { TableAction } from '../common/Table';
import { Archive, Clock, Mail, RotateCcw, Search, Trash2, UserCog, Star, MessageSquareText, Check, User, FileText, KeyRound } from 'lucide-react';
import { MemberStatusConstants } from '../../constants/MemberStatusConstants';

interface MemberActionHandlers {
  onView: (member: MemberDTO) => void;
  onViewMemberDetails: (member: MemberDTO) => void;
  onDelete: (member: MemberDTO) => Promise<void>;
  onSendEmail: (member: MemberDTO) => void;
  onViewRecordEmails: (member: MemberDTO) => void;
  onArchive: (member: MemberDTO) => Promise<void>;
  onRestore: (member: MemberDTO) => Promise<void>;
  onRestoreFromArchive: (member: MemberDTO) => Promise<void>;
  onImpersonateMemberLogin: (member: MemberDTO) => void;
  onChangeStatus?: (member: MemberDTO) => void;
  onAddNote: (member: MemberDTO) => void;
  onViewNoteHistory: (member: MemberDTO) => void;
  onViewInvoiceHistory: (member: MemberDTO) => void;
  onSendProofOfPaymentLink: (member: MemberDTO) => void;
  onSendPasswordResetLink: (member: MemberDTO) => void;
  onCompleteEftPayment: (member: MemberDTO) => void;
  onViewMandate: (member: MemberDTO) => void;
  onEditMandate: (member: MemberDTO) => void;
}

const createMemberActions =
  (handlers: MemberActionHandlers) =>
    (row: MemberDTO): TableAction[] => {
      const actions: TableAction[] = [];

      if (!row.isDeleted) {
        actions.push({
          label: 'View Member',
          onClick: () => handlers.onViewMemberDetails(row),
          icon: <User className="w-4 h-4" />,
        });
      }
      if (!row.isDeleted) {
        actions.push({
          label: 'Login as Member',
          onClick: () => handlers.onImpersonateMemberLogin(row),
          icon: <UserCog className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to login as this member?',
        });
      }
      if (!row.isDeleted) {
        actions.push({
          label: 'Send Message',
          onClick: () => handlers.onSendEmail(row),
          icon: <MessageSquareText className="w-4 h-4" />,
        });
      }
      if (!row.isDeleted) {
        actions.push({
          label: 'Messages(Record)',
          onClick: () => handlers.onViewRecordEmails(row),
          icon: <Clock className="w-4 h-4" />,
        });
      }
      if (!row.isDeleted) {
        actions.push({
          label: 'Create note',
          onClick: () => handlers.onAddNote(row),
          icon: <MessageSquareText className="w-4 h-4" />,
        });
      }
      actions.push({
        label: 'Notes History',
        onClick: () => handlers.onViewNoteHistory(row),
        icon: <Clock className="w-4 h-4" />,
      });
      if (!row.isDeleted) {
        actions.push({
          label: 'Email Proof Upload?',
          onClick: () => handlers.onSendProofOfPaymentLink(row),
          icon: <Mail className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to send the proof of payment link to this member?',
        });
      }
      if (!row.isDeleted) {
        actions.push({
          label: 'Send Password Reset Link',
          onClick: () => handlers.onSendPasswordResetLink(row),
          icon: <KeyRound className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to send a password reset link to this member?',
        });
      }
      if (!row.isDeleted && row.memberStatusId?.toLowerCase() === MemberStatusConstants.NEW_REGISTRATION) {
        actions.push({
          label: 'Paid',
          onClick: () => handlers.onCompleteEftPayment(row),
          icon: <Check className="w-4 h-4" />,
          className: 'text-green-600 hover:text-green-700',
          confirmMessage: 'Are you sure you want to mark this member as paid?',
        });
      }
      if (!row.isDeleted && !row.isArchived && handlers.onChangeStatus) {
        actions.push({
          label: 'Change Status',
          onClick: () => handlers.onChangeStatus!(row),
          icon: <Star className="w-4 h-4" />,
          className: 'text-orange-600 hover:text-orange-700',
        });
      }
      if (!row.isDeleted) {
        actions.push({
          label: 'Invoice History',
          onClick: () => handlers.onViewInvoiceHistory(row),
          icon: <Clock className="w-4 h-4" />,
        });

        if (row.isDebitOrderPayment) {
          actions.push({
            label: 'View Debit Order Mandate',
            onClick: () => handlers.onViewMandate(row),
            icon: <FileText className="w-4 h-4" />,
          });
          actions.push({
            label: 'Edit Debit Order Mandate',
            onClick: () => handlers.onEditMandate(row),
            icon: <FileText className="w-4 h-4" />,
          });
        }
      }
      if (!row.isDeleted) {
        actions.push({
          label: 'Delete Member',
          onClick: () => handlers.onDelete(row),
          icon: <Trash2 className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to delete this member?',
          className: 'text-red-600 hover:text-red-700',
        });
      }

      if (row.isDeleted) {
        actions.push({
          label: 'Restore Deleted',
          onClick: () => handlers.onRestore(row),
          icon: <RotateCcw className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to restore this member?',
          className: 'text-green-600 hover:text-green-700',
        });
      }

      if (row.isArchived) {
        actions.push({
          label: 'Restore Archive',
          onClick: () => handlers.onRestoreFromArchive(row),
          icon: <RotateCcw className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to restore this archive?',
          className: 'text-green-600 hover:text-green-700',
        });
      } else {
        actions.push({
          label: 'Archive Member',
          onClick: () => handlers.onArchive(row),
          icon: <Archive className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to archive this member?',
          className: 'text-red-600 hover:text-red-700',
        });
      }

      return actions;
    };

export default createMemberActions;
